import { useState, useCallback, useEffect } from 'react';
import { useSupabaseClient, useSession } from '@supabase/auth-helpers-react';
import { InvoiceSidebarData, SidebarAllocation, SidebarPriorInvoice } from '../../config';

export interface UseInvoiceSidebarResult {
  allocations: SidebarAllocation[];
  priorInvoices: SidebarPriorInvoice[];
  loading: boolean;
  refetch: () => Promise<void>;
}

/**
 * Loads read-only reference data for the invoice editor's left sidebar:
 * allocated time entries scoped to the invoice's project/stage and a slim
 * list of other invoices on the same project/stage. Single round-trip via
 * the get_invoice_sidebar RPC.
 */
export function useInvoiceSidebar(invoiceId: string | undefined): UseInvoiceSidebarResult {
  const supabase = useSupabaseClient();
  const session = useSession();

  const [allocations, setAllocations] = useState<SidebarAllocation[]>([]);
  const [priorInvoices, setPriorInvoices] = useState<SidebarPriorInvoice[]>([]);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    if (!invoiceId) return;
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_invoice_sidebar', {
        p_invoice_id: parseInt(invoiceId),
      });
      if (error) throw error;
      const payload = data as InvoiceSidebarData | null;
      setAllocations(payload?.allocations ?? []);
      setPriorInvoices(payload?.prior_invoices ?? []);
    } catch (err) {
      console.error('Error fetching invoice sidebar data:', err);
    } finally {
      setLoading(false);
    }
  }, [invoiceId, supabase]);

  useEffect(() => {
    if (session?.user) {
      void refetch();
    }
  }, [session, refetch]);

  return { allocations, priorInvoices, loading, refetch };
}
